import React, { useLayoutEffect, useRef, useState } from 'react'
import styles from './styles.module.css'

interface IDockTooltipProps {
  id: string
  label: string
  index: number | null
  itemsRef: React.RefObject<HTMLDivElement>
}

// One persistent label follows resting slots, independent of the magnified button bounds.
export function DockTooltip({ id, label, index, itemsRef }: IDockTooltipProps) {
  const [position, setPosition] = useState<{ x: number; width: number } | null>(null)
  const [lastLabel, setLastLabel] = useState(label)
  const textRef = useRef<HTMLSpanElement>(null)

  useLayoutEffect(() => {
    if (index === null) return

    const items = itemsRef.current
    const text = textRef.current
    const slot = items?.children[index] as HTMLElement | undefined
    if (!items || !text || !slot) return

    const measure = () => {
      const width = Math.min(text.scrollWidth + 20, items.clientWidth)
      const center = slot.offsetLeft + slot.offsetWidth / 2
      const x = Math.max(0, Math.min(center - width / 2, items.clientWidth - width))

      setPosition({ x, width })
      setLastLabel(label)
    }

    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(items)

    return () => observer?.disconnect()
  }, [index, itemsRef, label])

  return (
    <>
      <span className={styles.tooltipMeasure} ref={textRef} aria-hidden="true">
        {label}
      </span>
      {position && (
        <div
          id={id}
          role="tooltip"
          aria-hidden={index === null}
          className={styles.tooltip}
          data-visible={index !== null}
          style={{ width: position.width, transform: `translateX(${position.x}px)` }}
        >
          <span key={lastLabel} className={styles.tooltipLabel}>
            {lastLabel}
          </span>
        </div>
      )}
    </>
  )
}
